import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuLogOut, LuMail, LuCheck } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import SectionHeader from '../../components/ui/SectionHeader/SectionHeader';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import TextField from '../../components/ui/TextField/TextField';
import PhoneField from '../../components/ui/PhoneField/PhoneField';
import ResponsiveDialog from '../../components/ui/ResponsiveDialog/ResponsiveDialog';
import { useAuth } from '../../context/AuthContext';
import authClient from '../../auth/authClient';
import {
  requestEmailChangeRequest,
  verifyEmailChangeRequest,
} from '../../api/authApi';
import './ProfilePage.less';

// Simple, lenient checks used only to gate the Save button — the
// real validation lives on the backend. Trimmed values only.
function normalise(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function isEmail(v) {
  const s = normalise(v);
  if (!s) return false;
  // Deliberately permissive: RFC-perfect regexes reject valid
  // addresses. We only care that the user typed a plausible email;
  // authoritative validation happens server-side.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function ProfilePage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  // Initial values baked from the user prop so the dirty check can
  // compare against a canonical baseline. Any successful save
  // updates the baseline via `setUser` → re-derived here.
  const initial = useMemo(() => ({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
  }), [user?.firstName, user?.lastName, user?.phone]);

  const [firstName, setFirstName] = useState(initial.firstName);
  const [lastName, setLastName] = useState(initial.lastName);
  // The phone field reports `{ e164, raw, valid, empty, message }`; null
  // until the user edits it, so an untouched stored number is sent back
  // exactly as stored.
  const [phoneEdit, setPhoneEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);

  // Reset field state whenever the underlying user record changes —
  // this covers first-mount, a background /auth/me refresh, or the
  // post-save `setUser` that flips the baseline.
  useEffect(() => {
    setFirstName(initial.firstName);
    setLastName(initial.lastName);
    setPhoneEdit(null);
  }, [initial]);

  const isDirty =
    normalise(firstName) !== normalise(initial.firstName) ||
    normalise(lastName) !== normalise(initial.lastName) ||
    (phoneEdit !== null && normalise(phoneEdit.raw) !== normalise(initial.phone));

  // Phone is optional: empty is fine, anything else must be a valid number.
  const phoneInvalid = phoneEdit !== null && !phoneEdit.empty && !phoneEdit.valid;
  // Saved as E.164; cleared when emptied; untouched → as stored.
  const phoneToSave = phoneEdit === null
    ? (normalise(initial.phone) || null)
    : (phoneEdit.empty ? null : phoneEdit.e164);

  function validate() {
    const errs = {};
    if (!normalise(firstName)) errs.firstName = 'First name is required.';
    if (phoneInvalid) errs.phone = phoneEdit.message;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function onSave(e) {
    e.preventDefault();
    if (busy || !isDirty) return;
    setError(null);
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await authClient.patch('/auth/me', {
        firstName: normalise(firstName) || null,
        lastName: normalise(lastName) || null,
        phone: phoneToSave,
      });
      if (data?.user) setUser(data.user);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not save changes. Please try again.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function onSignOut() {
    try { await logout(); } catch { /* best-effort */ }
    navigate('/auth', { replace: true });
  }

  const currentEmail = user?.email || '';

  return (
    <Page>
      <PageHeader
        title="Profile"
        subtitle="Your contact details on GreenCharge."
        action={
          <Button
            variant="ghost"
            leadingIcon={<LuLogOut />}
            onClick={() => setConfirmingSignOut(true)}
          >
            Sign out
          </Button>
        }
      />

      <SectionHeader
        title="Personal information"
        description="Update your personal and contact details."
      />
      <Card>
        <form onSubmit={onSave} noValidate className="gc-profile-form">
          <div className="gc-profile-grid">
            <TextField
              label="First name"
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={validate}
              autoComplete="given-name"
              disabled={busy}
              error={fieldErrors.firstName}
              required
            />
            <TextField
              label="Last name"
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              disabled={busy}
            />
          </div>

          <PhoneField
            label="Mobile number"
            name="phone"
            value={initial.phone}
            onChange={setPhoneEdit}
            disabled={busy}
            error={fieldErrors.phone}
          />

          {/* Email is a read-only account row now — the disabled input
              lied about the affordance (it looked editable). See the
              secure change-email flow below. */}
          <div className="gc-profile-email-row">
            <div className="gc-profile-email-text">
              <p className="gc-profile-email-label">Email address</p>
              <p className="gc-profile-email-value" title={currentEmail}>
                {currentEmail || '—'}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leadingIcon={<LuMail />}
              onClick={() => setChangingEmail(true)}
              disabled={busy}
            >
              Change
            </Button>
          </div>

          {error && (
            <p className="gc-profile-error" role="alert">{error}</p>
          )}

          <div className="gc-profile-actions">
            {saved && (
              <span className="gc-profile-saved" aria-live="polite">
                <LuCheck aria-hidden="true" /> Saved
              </span>
            )}
            <Button type="submit" busy={busy} disabled={!isDirty || busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </Card>

      <ResponsiveDialog
        open={confirmingSignOut}
        onClose={() => setConfirmingSignOut(false)}
        title="Sign out?"
        description="You'll need to enter your email again to sign back in."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmingSignOut(false)}>Cancel</Button>
            <Button variant="primary" onClick={onSignOut}>Sign out</Button>
          </>
        }
      />

      <ChangeEmailDialog
        open={changingEmail}
        currentEmail={currentEmail}
        onClose={() => setChangingEmail(false)}
        onSuccess={(nextUser) => {
          if (nextUser) setUser(nextUser);
          setChangingEmail(false);
        }}
      />
    </Page>
  );
}

// ── Secure change-email dialog ─────────────────────────────────
//
// Two-step flow keyed off the backend contract in authApi.js:
//   Step 1 (enter): user types the new email → we request an OTP.
//   Step 2 (verify): user enters the OTP that arrived at the new
//     address → we complete the change and refresh the user record.
//
// The previous email address remains active on the account until
// step 2 succeeds — the backend owns that invariant, but the UI
// mirrors it so we never claim the swap happened before it did.
//
// Rate limits, expiry, single-use, and attempt caps are all
// server-side; we surface the resulting error codes verbatim so
// the user sees "code expired" / "too many attempts" without any
// client-side simulation.

function ChangeEmailDialog({ open, currentEmail, onClose, onSuccess }) {
  const [step, setStep] = useState('enter'); // 'enter' | 'verify' | 'success'
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('enter');
      setNewEmail('');
      setOtp('');
      setChallengeId(null);
      setError(null);
      setUnavailable(false);
      setBusy(false);
    }
  }, [open]);

  const normalisedNew = normalise(newEmail).toLowerCase();
  const canSubmitEnter =
    isEmail(normalisedNew) &&
    normalisedNew !== normalise(currentEmail).toLowerCase();

  async function requestOtp(e) {
    if (e?.preventDefault) e.preventDefault();
    if (busy || !canSubmitEnter) return;
    setError(null);
    setBusy(true);
    try {
      const data = await requestEmailChangeRequest({ newEmail: normalisedNew });
      setChallengeId(data?.challengeId || null);
      setStep('verify');
    } catch (err) {
      const code = err?.response?.data?.error;
      const status = err?.response?.status;
      if (status === 404) {
        // Endpoint not deployed yet. Surface a clean, honest state
        // instead of a raw stack trace — the UI is ready as soon as
        // the backend lands.
        setUnavailable(true);
      } else if (code === 'EMAIL_TAKEN') {
        // Deliberately generic so we don't confirm/deny another
        // account owns this email.
        setError('That email cannot be used. Try a different one.');
      } else if (code === 'RATE_LIMITED') {
        setError('Too many requests. Please try again in a few minutes.');
      } else {
        setError(err?.response?.data?.message || 'Could not send the code. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(e) {
    if (e?.preventDefault) e.preventDefault();
    const trimmed = normalise(otp);
    if (busy || !challengeId || trimmed.length < 4) return;
    setError(null);
    setBusy(true);
    try {
      const data = await verifyEmailChangeRequest({ challengeId, otp: trimmed });
      setStep('success');
      // Give the success state a beat to register before dismissing.
      setTimeout(() => onSuccess?.(data?.user), 1200);
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'OTP_EXPIRED') {
        setError('That code has expired. Request a new one.');
      } else if (code === 'OTP_INVALID') {
        setError('That code is not correct.');
      } else if (code === 'TOO_MANY_ATTEMPTS') {
        setError('Too many attempts. Please wait a moment and try again.');
      } else {
        setError(err?.response?.data?.message || 'Could not verify the code.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (unavailable) {
    return (
      <ResponsiveDialog
        open={open}
        onClose={onClose}
        size="sm"
        title="Change email"
        description="Self-service email changes are being enabled. In the meantime, contact support to update your address."
        dismissable
        footer={
          <Button variant="primary" onClick={onClose}>Close</Button>
        }
      />
    );
  }

  return (
    <ResponsiveDialog
      open={open}
      onClose={busy ? () => {} : onClose}
      size="sm"
      title={
        step === 'success' ? 'Email updated'
        : step === 'verify' ? 'Verify new email'
        : 'Change email'
      }
      description={
        step === 'success'
          ? 'Your account email has been updated.'
        : step === 'verify'
          ? `Enter the 6-digit code we sent to ${maskEmail(normalisedNew)}.`
        : 'We\'ll send a one-time code to your new email address to verify it.'
      }
      dismissable={!busy}
    >
      {step === 'enter' && (
        <form onSubmit={requestOtp} className="gc-profile-email-form" noValidate>
          <TextField
            label="New email address"
            name="newEmail"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={busy}
            required
          />
          {error && (
            <p className="gc-profile-error" role="alert">{error}</p>
          )}
          <div className="gc-profile-email-actions">
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" busy={busy} disabled={!canSubmitEnter || busy}>
              Send code
            </Button>
          </div>
        </form>
      )}

      {step === 'verify' && (
        <form onSubmit={verifyOtp} className="gc-profile-email-form" noValidate>
          <TextField
            label="Verification code"
            name="otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="123456"
            disabled={busy}
            required
          />
          {error && (
            <p className="gc-profile-error" role="alert">{error}</p>
          )}
          <div className="gc-profile-email-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setStep('enter'); setOtp(''); setError(null); }}
              disabled={busy}
            >
              Back
            </Button>
            <Button
              type="submit"
              busy={busy}
              disabled={busy || normalise(otp).length < 4}
            >
              Verify
            </Button>
          </div>
        </form>
      )}

      {step === 'success' && (
        <div className="gc-profile-email-success" aria-live="polite">
          <LuCheck aria-hidden="true" />
          <p>Your account is now using <strong>{normalisedNew}</strong>.</p>
        </div>
      )}
    </ResponsiveDialog>
  );
}

// Masks the local part of an email for display in the OTP step —
// enough hint that the user recognises it, but not the full string.
function maskEmail(v) {
  const s = normalise(v);
  const at = s.indexOf('@');
  if (at < 2) return s;
  const head = s.slice(0, 2);
  const tail = s.slice(at);
  return `${head}${'•'.repeat(3)}${tail}`;
}

export default ProfilePage;
